/**
 * @file DealsListPage.tsx
 * @description Реестр сделок (phase09; phase19.31 — макет (в) разбора A,
 * замечание владельца «Сделки тоже как Заказы»).
 *
 * Страница — раскладка: заголовок в верхней панели (`CabinetPageHeader`) с
 * подписью «16 сделок · 11 открытых» и главным действием «+ Сделка», строка
 * фильтров, таблица от 1024 / компактный список на телефоне
 * (`ResponsiveList`), постраничный вывод. Фильтры и страница — в адресе
 * (`useDealsRegistry`), колонки — `DEAL_REGISTRY_COLUMNS`.
 *
 * Плиток, цветной полосы слева у строки и «RUB» больше нет: стадия —
 * пилюлей по виду стадии, цвет магазина — точкой, сумма — «128 000 ₽».
 */

import { Kanban, Plus, RefreshCw, Receipt, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useHasPermission } from "../../../../auth/hooks";
import { CrmPagination } from "../../../../components/crm/CrmPagination";
import { DEAL_REGISTRY_COLUMNS } from "../../../../components/crm/deals/deal-columns";
import { DealCreateDialog } from "../../../../components/crm/deals/DealCreateDialog";
import { DealsFilterBar } from "../../../../components/crm/deals/DealsFilterBar";
import { useDealsRegistry } from "../../../../components/crm/deals/useDealsRegistry";
import { Button } from "../../../../components/ui/button";
import { CabinetPageHeader } from "../../../../components/ui/CabinetPageHeader";
import type { PageAction } from "../../../../components/ui/page-actions";
import { ResponsiveList } from "../../../../components/ui/ResponsiveList";
import { dealsQueryFiltered } from "../../../../lib/crm-deals-query";
import { pluralRu } from "../../../../lib/plural";

/** «16 сделок · 11 открытых»; пока счётчики не пришли — без подписи. */
function dealsSubtitle(counts: { total: number; open: number } | null): string | undefined {
  if (!counts) return undefined;
  const total = `${counts.total} ${pluralRu(counts.total, ["сделка", "сделки", "сделок"])}`;
  return `${total} · ${counts.open} ${pluralRu(counts.open, ["открытая", "открытые", "открытых"])}`;
}

export function DealsListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const base = `/shop/${slug}/crm`;
  const canUse = useHasPermission("SHOP_USE_CRM");
  const registry = useDealsRegistry();
  const { query, setQuery } = registry;
  const [creating, setCreating] = useState(false);

  const pipelineOptions = useMemo(
    () => [{ value: "", label: "Все воронки" }, ...registry.pipelines.map((p) => ({ value: p.id, label: p.name }))],
    [registry.pipelines],
  );
  const menu: PageAction[] = [
    { key: "board", label: "Доска сделок", icon: Kanban, to: `${base}/board` },
    { key: "refresh", label: "Обновить список", icon: RefreshCw, run: registry.refresh, loading: registry.loading },
  ];
  const createButton = canUse ? (
    <Button variant="brand" onClick={() => setCreating(true)} disabled={registry.pipelines.length === 0}>
      <Plus className="size-4" />
      Сделка
    </Button>
  ) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <CabinetPageHeader
        title="Сделки"
        subtitle={dealsSubtitle(registry.counts)}
        menu={menu}
        menuLabel="Другие действия со сделками"
        action={createButton}
      />

      <DealsFilterBar query={query} onChange={setQuery} pipelineOptions={pipelineOptions} />

      <ResponsiveList
        aria-label="Реестр сделок"
        rows={registry.items}
        columns={DEAL_REGISTRY_COLUMNS}
        rowKey={(deal) => deal.id}
        rowHref={(deal) => `${base}/deals/${deal.id}`}
        rowLabel={(deal) => `Сделка «${deal.displayName}»`}
        rowSize="double"
        compactWrap
        loading={registry.loading}
        error={registry.error}
        onRetry={registry.refresh}
        filtered={dealsQueryFiltered(query)}
        skeletonRows={10}
        empty={{
          icon: Receipt,
          title: "Сделок пока нет",
          description: canUse
            ? "Сделка — переговоры с клиентом до заказа. Создайте первую кнопкой «+ Сделка»"
            : "Сделка — переговоры с клиентом до заказа",
        }}
        filteredEmpty={{
          icon: Search,
          title: "Нет сделок по выбранным условиям",
          description: query.openOnly ? "Отбор «Только открытые» скрывает закрытые сделки" : undefined,
          onReset: registry.resetFilters,
        }}
      />

      <CrmPagination
        label="Страницы реестра сделок"
        page={query.page}
        pageSize={query.pageSize}
        total={registry.total}
        onPageChange={(page) => setQuery({ page })}
        onPageSizeChange={(pageSize) => setQuery({ pageSize })}
      />

      {creating ? (
        <DealCreateDialog
          pipelines={registry.pipelines}
          initialPipelineId={query.pipelineId}
          onClose={() => setCreating(false)}
          onCreated={(deal) => {
            setCreating(false);
            navigate(`${base}/deals/${deal.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
